// src/pages/CustomerCare.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Common/header/Navigation";
import Footer from "../components/Common/footer/Footer";
import "../styles/Pages.css";

const CustomerCare = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "General Inquiry",
        orderId: "",
        message: "",
    });

    const [status, setStatus] = useState("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMessage("");

        // Smart routing for the Contact API
        const API_BASE_URL = window.location.hostname === 'localhost'
            ? 'http://localhost:5001'
            : 'https://api.bundlemadnesshair.com';

        try {
            const response = await fetch(`${API_BASE_URL}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || "Failed to send message");
            setStatus("success");
        } catch (error) {
            console.error("Submission error:", error);
            setStatus("error");
            setErrorMessage("We encountered an issue sending your message. Please try again or email us directly.");
        }
    };

    const needsOrderId = formData.subject === "Return / Exchange" || formData.subject === "Order Status";

    return (
        <div className="page-shell">
            <Navigation />

            {/* ── Hero ── */}
            <section className="page-hero">
                <div className="page-hero-border" />
                <div className="container" style={{ position: "relative", zIndex: 1 }}>
                    <span className="page-hero-eyebrow">We're here to help</span>
                    <h1 className="page-hero-title">Customer Care</h1>
                    <p className="page-hero-subtitle">
                        Our concierge team is here to assist you with any enquiry
                    </p>
                    <div className="page-hero-rule" />
                </div>
            </section>

            {/* ── Form ── */}
            <section className="page-section">
                <div className="container">
                    {status === "success" ? (
                        <div className="form-panel">
                            <div className="form-success">
                                <div className="form-success-icon">✓</div>
                                <h2 className="form-success-title">Request Received</h2>
                                <p className="form-success-text">
                                    Thank you for contacting Bundle Madness. Our Customer Care team will review your
                                    message and respond to the email provided within 24–48 business hours.
                                </p>
                                <button
                                    className="btn-luxury"
                                    onClick={() => {
                                        setStatus("idle");
                                        setFormData(prev => ({ ...prev, message: "", orderId: "" }));
                                    }}
                                >
                                    <span>Send Another Message</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="form-panel">
                            <h3 className="form-panel-title">Get in Touch</h3>

                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="field-group">
                                            <label className="field-label">Full Name *</label>
                                            <input type="text" name="name" value={formData.name} onChange={handleChange} className="field-input" required />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="field-group">
                                            <label className="field-label">Email Address *</label>
                                            <input type="email" name="email" value={formData.email} onChange={handleChange} className="field-input" required />
                                        </div>
                                    </div>
                                </div>

                                <div className="field-group">
                                    <label className="field-label">How can we help? *</label>
                                    <select name="subject" value={formData.subject} onChange={handleChange} className="field-select">
                                        <option value="General Inquiry">General Product Inquiry</option>
                                        <option value="Order Status">Check Order Status</option>
                                        <option value="Return / Exchange">Initiate a Return or Exchange</option>
                                        <option value="Styling Advice">Styling &amp; Hair Care Advice</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                {needsOrderId && (
                                    <div className="field-group" style={{ animation: "fadeIn 0.3s ease" }}>
                                        <label className="field-label">Order Number *</label>
                                        <input type="text" name="orderId" value={formData.orderId} onChange={handleChange} className="field-input" placeholder="e.g. #BM12345" required />
                                        <p className="field-note">Please provide your order number to help us expedite your request.</p>
                                    </div>
                                )}

                                <div className="field-group">
                                    <label className="field-label">Message *</label>
                                    <textarea name="message" value={formData.message} onChange={handleChange} className="field-textarea" placeholder="Please provide as much detail as possible..." required />
                                </div>

                                {status === "error" && (
                                    <div className="form-error">{errorMessage}</div>
                                )}

                                <button type="submit" className="btn-luxury" style={{ width: "100%" }} disabled={status === "loading"}>
                                    <span>{status === "loading" ? "Sending Request..." : "Submit Request"}</span>
                                </button>

                                <p style={{ textAlign: "center", marginTop: "20px", fontFamily: "var(--font-body)", fontSize: "0.72rem", color: "var(--text-muted)", letterSpacing: "1px" }}>
                                    For return eligibility, please refer to our{" "}
                                    <Link to="/terms" style={{ color: "var(--gold)", textDecoration: "underline" }}>Return Policy</Link>.
                                </p>
                            </form>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default CustomerCare;